import cn from 'classnames'

import { useEffect, useRef, useState } from 'react'

import Link from 'next/link'

import { LinkButton } from 'components/ui/link-button'
import IconArrow from 'images/icon-arrow.svg'

import Skeleton from 'components/ui/skeleton'

import { useTranslation } from 'utils/use-translation'

import { fetchTransactions } from 'store/fetchers/users'

import ContentHeader from '../../../components/ui/account/content/__header'
import { useHeader } from '../../../utils/use-header'

import { useAppDispatch, useAppSelector } from '../../../store/hooks'

import { allTransactions, allWallets, fetchWallets, setAllTransactions } from '../../../store/reducers/wallets'

import { getTotalAmountOfBalance } from '../../../store/fetchers/loans'

import { selectedUser, selectedUserSummary } from '../../../store/reducers/users'

import { fetchLoans, getLoans } from '../../../store/reducers/loans'

import { IFormattedLoan } from '../../../store/fetchers/loans/enum'

import { IFormattedEarning } from '../../../store/fetchers/earnings/enum'

import { fetchUserDeposits } from '../../../store/fetchers/earnings'

import { floorNumber } from '../../../utils/floor-number'

import { countDecimalPlaces } from '../../../utils/count-decimal-places'

import { ITransaction } from '../../../store/fetchers/users/types'

import ZeroBalance from './images/zero-balance.svg'

import DashboardLoans from './dashboard-loans'

import DashboardPieChart from './dashboard-pie-chart'
import DashboardPieDescription from './dashboard-pie-description'

import styles from './styles.module.scss'
import DashboardSavings from './dashboard-savings'
import { TransactionTab } from './transaction-tab'
import DashboardOperationsCard from './operations-card'
import LoyaltyProgram from './loyalty-program'
import { UnlockedVipStatus } from './unlocked-vip-status'

export default function DashboardPage() {
  const { t } = useTranslation()

  const head = useHeader(t('dashboard'))
  const wallets = useAppSelector(allWallets)
  const storageTransactions = useAppSelector(allTransactions)

  const [totalWalletAmount, setTotalWalletAmount] = useState<number>(0)
  const dispatch = useAppDispatch()
  const user = useAppSelector(selectedUser)
  const isWalletsFetched = useRef(false)
  const isLoansFetched = useRef(false)
  const isSavingsFetched = useRef(false)
  const isStatusesFetched = useRef(false)
  const userSummary = useAppSelector(selectedUserSummary)

  const loans: IFormattedLoan[] = useAppSelector(getLoans)
  const [savings, setSavings] = useState<IFormattedEarning[]>([])
  const [isWalletLoading, setIsWalletsLoading] = useState<boolean>(true)
  const [isLoansLoading, setIsLoansLoading] = useState<boolean>(true)
  const [isSavingsLoading, setIsSavingLoading] = useState<boolean>(true)
  const [totalSavingAmount, setTotalSavingAmount] = useState<number>(0)
  const [tabsVisibility, setTabsVisibility] = useState({
    tab0: true,
    tab1: true,
    tab2: true,
    tab3: true,
  })
  const [transactions, setTransactions] = useState<ITransaction[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [totalLoanSum, setTotalLoanSum] = useState<string>('')
  const [isLoyaltyLoading, setIsLoyaltyLoading] = useState<boolean>(true)

  const isFetchedTransactions = useRef<boolean>(false)

  const fetchAllTransactions = async () => {
    setIsLoading(true)
    const res = await fetchTransactions({})

    if (res?.length) {
      dispatch(setAllTransactions(res))
    }

    setIsLoading(false)
  }

  useEffect(() => {
    if (userSummary) {
      setTotalLoanSum(userSummary.loan.loanAmountReducedInUsdt)
      setIsLoyaltyLoading(false)
    }
  }, [userSummary])

  useEffect(() => {
    if (user && !isWalletsFetched.current) {
      isWalletsFetched.current = true
      getWallets()
    }

    if (user && !isSavingsFetched.current) {
      isSavingsFetched.current = true
      getSavings()
    }

    if (user && !isLoansFetched.current) {
      isLoansFetched.current = true
      getLoansData()
      getTotalAmountOfWallets()
    }

    if (user && !isStatusesFetched.current) {
      isStatusesFetched.current = true
    }

    if (!isFetchedTransactions.current && user) {
      isFetchedTransactions.current = true
      fetchAllTransactions()
    }
  }, [user])

  useEffect(() => {
    setTransactions(storageTransactions)
  }, [storageTransactions])

  useEffect(() => {
    if (savings.length !== 0) {
      calculateTotalSavingAmount()
    }
  }, [savings])

  const getSavings = async () => {
    const data = await fetchUserDeposits()
    setIsSavingLoading(false)
    setSavings(data)
  }

  const getTotalAmountOfWallets = async () => {
    const data = await getTotalAmountOfBalance()
    setTotalWalletAmount(+data.amount)
  }

  const getWallets = async () => {
    await dispatch(fetchWallets())
    setIsWalletsLoading(false)
  }

  const getLoansData = async () => {
    await dispatch(fetchLoans())
    setIsLoansLoading(false)
  }

  const saveTabsStateToLocal = (tabsState) => {
    if (typeof window !== 'undefined') {
      localStorage?.setItem('tabsVisibility', JSON.stringify(tabsState))
    }
  }

  useEffect(() => {
    if (typeof window !== 'undefined' && window.localStorage) {
      const savedTabsState = localStorage?.getItem('tabsVisibility')
      if (savedTabsState) {
        setTabsVisibility(JSON.parse(savedTabsState))
      }
    }
  }, [])

  useEffect(() => {
    saveTabsStateToLocal(tabsVisibility)
  }, [tabsVisibility])

  const toggleTabVisibility = (tab) => {
    setTabsVisibility((prevState) => ({
      ...prevState,
      [tab]: !prevState[tab],
    }))
  }

  const calculateTotalSavingAmount = () => {
    let amount = savings
      .filter(
        (item) =>
          item.status === 'ACTIVE' ||
          item.status === 'INCREASING' ||
          item.status === 'CHARGING' ||
          item.status === 'WAITING_WITHDRAW' ||
          item.status === 'WITHDRAWING' ||
          item.status === 'FAILED',
      )
      .reduce((acc, index) => (acc += +index.withdraw.available_amount), 0)
    amount = countDecimalPlaces(amount) > 6 ? floorNumber(amount, 6) : amount
    setTotalSavingAmount(amount)
  }

  useEffect(() => {
    if (!isWalletLoading && (wallets.length === 0 || +totalWalletAmount === 0)) {
      setTabsVisibility({
        ...tabsVisibility,
        tab1: true,
      })
    }
  }, [isWalletLoading, wallets, totalWalletAmount])

  useEffect(() => {
    if (!isLoansLoading && loans.filter((item) => item.status === 'order_created').length === 0) {
      setTabsVisibility({
        ...tabsVisibility,
        tab2: true,
      })
    }
  }, [isLoansLoading, loans])

  useEffect(() => {
    if (!isSavingsLoading && savings.length === 0) {
      setTabsVisibility({
        ...tabsVisibility,
        tab3: true,
      })
    }
  }, [isSavingsLoading, savings])

  return (
    <div className={styles.dashboard}>
      {head}
      <ContentHeader title={t('dashboard')}></ContentHeader>
      <p className={styles.description}>{t('dashboard_control')}</p>

      {user?.is_show_vip_banner && <UnlockedVipStatus />}

      {floorNumber(Number(totalLoanSum), 2) > 50000 && (
        <section className={cn(styles.mainBlock)}>
          <div className={cn(styles['mainBlock-header'], styles.lessMargin)}>
            <div className={styles['mainBlock-content']}>
              <h3 className={cn(styles.mainBlock__title, styles.nomargin)}>{t('join_loyalty_program')}</h3>
            </div>

            <div className={styles.mainBlock__arrow} onClick={() => toggleTabVisibility('tab0')}>
              <img
                alt="arrow"
                className={cn({
                  [styles.close]: !tabsVisibility.tab0,
                })}
                src={IconArrow}
              />
            </div>
          </div>
          <div className={cn(styles.mainBlock__block)}>
            <LoyaltyProgram
              user={user}
              totalSum={floorNumber(Number(totalLoanSum), 2)}
              isLoading={isLoyaltyLoading}
              isShort={!tabsVisibility.tab0}
            />
          </div>
        </section>
      )}

      <section className={styles.currentTransactions}>
        <DashboardOperationsCard toggleVisibility={() => toggleTabVisibility('tab0')} tabsVisibility={tabsVisibility} />
      </section>

      <section className={styles.mainBlock}>
        <div className={cn(styles['mainBlock-header'])}>
          <div className={styles['mainBlock-content']}>
            <Link passHref href="/account/wallets">
              <a className={cn(styles.mainBlock__title, styles.nomargin)}>{t('wallets')}</a>
            </Link>
            <p className={cn(styles.mainBlock__text, styles.mainBlock__text_onlyDesktop)}>{t('assets_portfolio')}</p>
            {!tabsVisibility.tab1 && (
              <p
                className={cn(
                  styles.mainBlock__text,
                  styles.mainBlock__text_closed,
                  styles.mainBlock__text_onlyDesktop,
                )}
              >
                {t('wallet_balance')}
                <span> ${floorNumber(totalWalletAmount, 2)}</span>
              </p>
            )}
          </div>
          {!isWalletLoading && wallets.length !== 0 && +totalWalletAmount !== 0 && (
            <div className={styles.mainBlock__arrow} onClick={() => toggleTabVisibility('tab1')}>
              <img
                alt="arrow"
                className={cn({
                  [styles.close]: !tabsVisibility.tab1,
                })}
                src={IconArrow}
              />
            </div>
          )}
        </div>
        <div className={styles.content}>
          <p>{t('assets_portfolio')}</p>
          {!tabsVisibility.tab1 && (
            <p className={cn(styles.mainBlock__text, styles.mainBlock__text_closed)}>
              {t('wallet_balance')}
              <span> ${floorNumber(totalWalletAmount, 2) ?? 0}</span>
            </p>
          )}
        </div>
        <LinkButton className={styles.linkBtn} title={t('go_wallets')} path={'/account/wallets'} />
        <div
          className={cn(
            styles.mainBlock__block,
            { [styles.mainBlock__hidden]: !tabsVisibility.tab1 },
            { [styles.mainBlock__visible]: tabsVisibility.tab1 },
            { [styles.mainBlock__nomargin]: !isWalletLoading && (wallets.length === 0 || +totalWalletAmount === 0) },
          )}
        >
          {!isWalletLoading ? (
            wallets.length !== 0 && +totalWalletAmount !== 0 ? (
              <>
                <div className={styles.mainBlock__wallet_desktop}>
                  <DashboardPieChart wallets={wallets} totalWalletBalance={totalWalletAmount} />
                  <DashboardPieDescription wallets={wallets} totalWalletBalance={totalWalletAmount} />
                </div>

                <div className={styles.mainBlock__wallet_mobile}>
                  <div className={styles.info}>
                    <DashboardPieChart wallets={wallets} totalWalletBalance={totalWalletAmount} />
                  </div>

                  <DashboardPieDescription wallets={wallets} totalWalletBalance={totalWalletAmount} />
                </div>
              </>
            ) : (
              <>
                <img className={styles.zeroBalance} src={ZeroBalance} />
              </>
            )
          ) : (
            <Skeleton className={styles.loader} />
          )}
        </div>
      </section>

      <section className={cn(styles.mainBlock)}>
        <div className={styles['mainBlock-header']}>
          <div className={styles['mainBlock-content']}>
            <Link href="/account/loans" passHref>
              <a className={styles.mainBlock__title}>{t('loans')}</a>
            </Link>
            <p className={styles.mainBlock__text}>{t('check_loans_info')}</p>
            {!tabsVisibility.tab2 && (
              <p className={cn(styles.mainBlock__text, styles.mainBlock__text_closed)}>
                {t('total_collateral')}
                <span> ${floorNumber(+userSummary?.loan?.depositAmountReducedInUsd, 2) ?? 0}</span>
              </p>
            )}
          </div>
          {!isLoansLoading && loans.filter((item) => item.status === 'order_created').length !== 0 && (
            <div className={styles.mainBlock__arrow} onClick={() => toggleTabVisibility('tab2')}>
              <img
                alt="arrow"
                className={cn({
                  [styles.close]: !tabsVisibility.tab2,
                })}
                src={IconArrow}
              />
            </div>
          )}
        </div>
        <LinkButton className={styles.linkBtn} title={t('take_loan')} path="/account/loans/add" />
        <div
          className={cn(
            styles.mainBlock__block,
            { [styles.mainBlock__hidden]: !tabsVisibility.tab2 },
            { [styles.mainBlock__visible]: tabsVisibility.tab2 },
            {
              [styles.mainBlock__nomargin]:
                !isLoansLoading && loans.filter((item) => item.status === 'order_created').length === 0,
            },
          )}
        >
          {!isLoansLoading ? (
            loans.filter((item) => item.status === 'order_created').length !== 0 ? (
              <DashboardLoans loans={loans} loansSummary={userSummary?.loan} />
            ) : wallets.length !== 0 && +totalWalletAmount !== 0 && !isWalletLoading ? (
              <>
                <img className={styles.zeroBalance} src={ZeroBalance} />
              </>
            ) : (
              <></>
            )
          ) : (
            <Skeleton className={styles.loader} />
          )}
        </div>
      </section>

      <section className={styles.mainBlock}>
        <div className={styles['mainBlock-header']}>
          <div className={styles['mainBlock-content']}>
            <Link passHref href="/account/savings">
              <a className={styles.mainBlock__title}>{t('savings')}</a>
            </Link>
            <p className={styles.mainBlock__text}>{t('saving_accounts_info')}</p>
            {!tabsVisibility.tab3 && (
              <p className={cn(styles.mainBlock__text, styles.mainBlock__text_closed)}>
                {t('total_amount')} <span>${totalSavingAmount ?? 0}</span>
              </p>
            )}
          </div>

          {!isSavingsLoading && savings.length !== 0 && (
            <div className={styles.mainBlock__arrow} onClick={() => toggleTabVisibility('tab3')}>
              <img
                alt="arrow"
                className={cn({
                  [styles.close]: !tabsVisibility.tab3,
                })}
                src={IconArrow}
              />
            </div>
          )}
        </div>
        <LinkButton className={styles.linkBtn} title={t('start_earning')} path="/account/savings/add" />
        <div
          className={cn(
            styles.mainBlock__block,
            { [styles.mainBlock__hidden]: !tabsVisibility.tab3 },
            { [styles.mainBlock__visible]: tabsVisibility.tab3 },
            {
              [styles.mainBlock__nomargin]: !isSavingsLoading && savings.length === 0,
            },
          )}
        >
          {!isSavingsLoading ? (
            savings.length !== 0 ? (
              <DashboardSavings
                savings={savings.filter(
                  (item) =>
                    item.status === 'ACTIVE' ||
                    item.status === 'INCREASING' ||
                    item.status === 'CHARGING' ||
                    item.status === 'WAITING_WITHDRAW' ||
                    item.status === 'WITHDRAWING' ||
                    item.status === 'FAILED',
                )}
              />
            ) : wallets.length !== 0 &&
              +totalWalletAmount !== 0 &&
              loans.filter((item) => item.status !== 'order_created').length === 0 &&
              !isWalletLoading &&
              !isLoansLoading ? (
              <>
                <img className={styles.zeroBalance} src={ZeroBalance} />
              </>
            ) : (
              <></>
            )
          ) : (
            <Skeleton className={styles.loader} />
          )}
        </div>
      </section>

      <TransactionTab
        transactions={transactions}
        setTransactions={setTransactions}
        isLoading={isLoading}
        setIsLoading={setIsLoading}
      />
    </div>
  )
}
